import { loadJSON, saveJSON } from '../../../utils/tabStorage'
import { standardId } from '../../../utils/standardRef'

// Pure comment-filter logic for the Code page's comments pane, mirroring the
// Library nav filter (`codeFilter.js`). A window's comment filter is plain
// serializable data; `makeCommentMatcher` turns it into a single predicate.
//
// Facet groups (each contributes one check when it has a selection):
//   - attrs   Mine / Public / Questions / Bookmarked (OR within the group)
//   - authors one or more authors (OR within the group)
//   - items   one or more tagged items (OR within the group), optionally rolled
//             down to their descendants
//   - topics  one or more topics the comment is filed under (OR within the group)
// An Any/All (`matchMode`) operator combines the active groups — exactly like
// the nav popover: within a facet the selections are OR'd, and the facets
// combine by Any (OR) or All (AND).
import { authorIdOf, isOwnComment, isSavedBy } from './commentUtils'

// Attribute facet options (id must stay stable — it is persisted).
export const ATTR_OPTIONS = [
  { id: 'mine', label: 'Mine' },
  { id: 'public', label: 'Public' },
  { id: 'questions', label: 'Questions' },
  { id: 'saved', label: 'Bookmarked' },
]

export const COMMENT_SORTS = [
  { id: 'latest', label: 'Latest' },
  { id: 'oldest', label: 'Oldest' },
  { id: 'likes', label: 'Most liked' },
  { id: 'least', label: 'Least liked' },
]

const VALID_ATTRS = new Set(ATTR_OPTIONS.map((o) => o.id))
const VALID_SORTS = new Set(COMMENT_SORTS.map((o) => o.id))

export function emptyCommentFilter() {
  return {
    attrs: [], // subset of ATTR_OPTIONS ids
    authors: [], // [{ _id, label }] (picker option objects)
    items: [], // [{ _id, label, standard, order }]
    topics: [], // [{ _id, name }]
    includeDescendants: false,
    matchMode: 'or', // 'or' | 'and'
    sort: 'latest',
  }
}

// Normalize a persisted (possibly partial/old) blob into a full filter.
export function coerceCommentFilter(raw) {
  const base = emptyCommentFilter()
  if (!raw || typeof raw !== 'object') return base
  return {
    attrs: Array.isArray(raw.attrs) ? raw.attrs.filter((a) => VALID_ATTRS.has(a)) : [],
    authors: Array.isArray(raw.authors) ? raw.authors : [],
    items: Array.isArray(raw.items) ? raw.items : [],
    topics: Array.isArray(raw.topics) ? raw.topics : [],
    includeDescendants: Boolean(raw.includeDescendants),
    matchMode: raw.matchMode === 'and' ? 'and' : 'or',
    sort: VALID_SORTS.has(raw.sort) ? raw.sort : 'latest',
  }
}

// A filter is "active" (drives the toolbar dot) when at least one facet has a
// selection. `includeDescendants` and `matchMode`/`sort` alone don't count —
// they only shape an existing selection.
export function isCommentFilterActive(filter) {
  const f = filter || emptyCommentFilter()
  return Boolean(f.attrs?.length || f.authors?.length || f.items?.length || f.topics?.length)
}

function attrPredicate(attr, userId) {
  switch (attr) {
    case 'mine':
      return (c) => isOwnComment(c, userId)
    case 'public':
      return (c) => Boolean(c.isPublic)
    case 'questions':
      return (c) => Boolean(c.isQuestion)
    case 'saved':
      return (c) => isSavedBy(c, userId)
    default:
      return () => false
  }
}

// One anchor matches a selected filter item when it's that same item, or (with
// descendants) a descendant of it — the roll-up rule the server uses: same
// standard AND (anchor.order === sel.order OR anchor.path includes sel.order).
function anchorMatchesSelection(anchor, sel, includeDescendants) {
  if (standardId(sel.standard) && standardId(anchor.standard) && standardId(anchor.standard) !== standardId(sel.standard)) return false
  if (String(anchor.item ?? anchor._id ?? '') === String(sel._id)) return true
  if (sel.order != null && anchor.order === sel.order) return true
  if (
    includeDescendants &&
    sel.order != null &&
    Array.isArray(anchor.path) &&
    anchor.path.includes(sel.order)
  ) {
    return true
  }
  return false
}

function matchesTopics(comment, topics) {
  const ids = new Set((comment.topics || []).map((t) => String(t?._id ?? t)))
  return topics.some((t) => ids.has(String(t?._id ?? t)))
}

function matchesItems(comment, items, includeDescendants) {
  const anchors = comment.items || []
  return items.some((sel) => anchors.some((a) => anchorMatchesSelection(a, sel, includeDescendants)))
}

// Build the single predicate for one filter. `userId` resolves the Mine /
// Bookmarked attributes against the current viewer.
export function makeCommentMatcher(filter, userId) {
  const f = coerceCommentFilter(filter)
  const attrPreds = f.attrs.map((a) => attrPredicate(a, userId))
  const authorIds = new Set(f.authors.map((a) => String(a?._id ?? a)))
  const { items, topics, includeDescendants, matchMode } = f

  const combine = (checks) => {
    if (!checks.length) return true
    return matchMode === 'and' ? checks.every(Boolean) : checks.some(Boolean)
  }

  const match = (comment) => {
    const checks = []
    if (attrPreds.length) checks.push(attrPreds.some((p) => p(comment)))
    if (authorIds.size) checks.push(authorIds.has(String(authorIdOf(comment))))
    if (items.length) checks.push(matchesItems(comment, items, includeDescendants))
    if (topics.length) checks.push(matchesTopics(comment, topics))
    return combine(checks)
  }

  return { active: isCommentFilterActive(f), match }
}

// --- per-window persistence ------------------------------------------------
// Stored as { [windowId]: filterBlob } under one key, mirroring gridStorage.
// Each window's comments pane reads/writes only its own slot.
const STORE_KEY = 'ct-code-comments-v1'

function readStore() {
  try {
    const parsed = loadJSON(STORE_KEY)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function loadCommentFilter(windowId) {
  if (typeof window === 'undefined' || !windowId) return emptyCommentFilter()
  return coerceCommentFilter(readStore()[windowId])
}

export function saveCommentFilter(windowId, filter) {
  if (typeof window === 'undefined' || !windowId) return
  try {
    const store = readStore()
    store[windowId] = filter
    saveJSON(STORE_KEY, store)
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}
